"use client";

import { m } from "framer-motion";
import { useRef } from "react";
import Image from "next/image";
import FadeIn from "../ui/FadeIn";
import Marquee from "../ui/Marquee";
import { cdnUrl } from "@/lib/cdn";

const testimonials = [
  {
    quote: "I use Hudson Labs to take what was a two or three hour process down to a 20-minute process.",
    name: "Brett Caughran",
    role: "Founder",
    company: "Fundamental Edge",
    avatar: "/images/testimonials/brett-caughran.png",
  },
  {
    quote: "It\u2019s easy to see the efficiency gains. Hudson Labs handles high-precision financial tasks where errors can be very costly.",
    name: "Buyside AI Reviews",
    role: "Independent Reviewer",
    company: "Buyside AI Reviews",
    avatar: "/images/testimonials/buyside-ai-reviews.webp",
  },
  {
    quote: "It\u2019s the best I\u2019ve seen.",
    name: "Tripp Blumm",
    role: "Partner",
    company: "Princeton Value Partners",
    avatar: "/images/testimonials/tripp-blumm.webp",
  },
  {
    quote: "I\u2019m blown away by what you\u2019ve built.",
    name: "Ken Taylor",
    role: "Founder",
    company: "Telos Research",
    avatar: "/images/testimonials/ken-taylor.webp",
  },
  {
    quote: "These tools are spot on and quickly get me up to speed.",
    name: "Will Henderson",
    role: "Private Investor",
    company: "",
    avatar: "/images/testimonials/will-henderson.webp",
  },
  {
    quote: "Thanks for building such an excellent tool for investors.",
    name: "Adam Parmer",
    role: "Managing Partner",
    company: "Enhancing Capital",
    avatar: "/images/testimonials/adam-parmer.webp",
  },
  {
    quote: "A significant process improvement for identifying securities fraud.",
    name: "Tom Elrod",
    role: "Partner",
    company: "Kirby McInerney LLP",
    avatar: "/images/testimonials/tom-elrod.jpg",
  },
];

const row1 = testimonials.slice(0, 4);
const row2 = testimonials.slice(4, 7);

function TestimonialCard({ t }: { t: (typeof testimonials)[0] }) {
  return (
    <m.div
      whileHover={{ y: -4 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      className="w-[250px] sm:w-[300px] md:w-[380px] shrink-0 p-4 sm:p-5 md:p-6 rounded-xl border border-gray-200/60 bg-[#f5f5f0] shadow-[0_2px_20px_-4px_rgba(0,0,0,0.08)] hover:bg-[#eeeeea] hover:border-gray-300/80 hover:shadow-[0_4px_30px_-4px_rgba(0,0,0,0.12)] transition-all duration-500 group"
    >
      {/* Quote mark */}
      <svg className="w-6 h-6 sm:w-8 sm:h-8 text-gray-900/10 mb-4 sm:mb-6" fill="currentColor" viewBox="0 0 24 24">
        <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10H14.017zM0 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151C7.546 6.068 5.983 8.789 5.983 11H10v10H0z" />
      </svg>

      <p className="text-gray-800 leading-relaxed text-[15px] sm:text-[16px] font-light mb-6 sm:mb-8">
        {t.quote}
      </p>

      <div className="flex items-center gap-4">
        {t.avatar ? (
          <Image
            src={cdnUrl(t.avatar)}
            alt={t.name}
            width={44}
            height={44}
            sizes="44px"
            loading="lazy"
            className="w-11 h-11 rounded-full object-cover"
          />
        ) : (
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center border border-gray-300">
            <span className="text-sm font-semibold text-gray-500">
              {t.name.split(" ").map((n) => n[0]).join("")}
            </span>
          </div>
        )}
        <div>
          <p className="text-sm font-medium text-gray-900">{t.name}</p>
          <p className="text-xs text-gray-500">
            {t.company ? `${t.role} at ${t.company}` : t.role}
          </p>
        </div>
      </div>
    </m.div>
  );
}

export default function Testimonials() {
  const ref = useRef<HTMLDivElement>(null);

  return (
    <section
      id="testimonials"
      ref={ref}
      className="pt-[2.8rem] sm:pt-[4.2rem] lg:pt-[5.6rem] xl:pt-[8.4rem] short:pt-8 shorter:pt-5 pb-16 sm:pb-24 lg:pb-32 xl:pb-48 short:pb-10 shorter:pb-6 bg-[#0a0a0a] text-white relative overflow-hidden"
    >
      {/* Grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:60px_60px]" />

      <div className="relative">
        {/* Header */}
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-8 mb-10 sm:mb-16 lg:mb-16 xl:mb-24">
          <FadeIn>
            <h2 className="text-4xl md:text-5xl xl:text-6xl font-[family-name:var(--font-display)] tracking-[-0.02em] text-white leading-[1.05]">
              What our clients say
            </h2>
          </FadeIn>
        </div>

        {/* Marquee rows */}
        <div className="space-y-5">
          <Marquee speed={55} pauseOnHover>
            {row1.map((t) => (
              <TestimonialCard key={t.name} t={t} />
            ))}
          </Marquee>
          <Marquee speed={48} direction="right" pauseOnHover>
            {row2.map((t) => (
              <TestimonialCard key={t.name} t={t} />
            ))}
          </Marquee>
        </div>
      </div>
    </section>
  );
}
